import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../stores/uiStore';
import { useSSEStore } from '../../stores/sseStore';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../api/client';
import { X, Bell, CheckCheck, AlertTriangle, Info, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';

interface Notification {
  id: number;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  metadata: Record<string, unknown>;
}

const TYPE_ICONS: Record<string, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
  success: CheckCircle2,
};

const TYPE_COLORS: Record<string, string> = {
  info: 'text-blue-500',
  warning: 'text-yellow-500',
  error: 'text-red-500',
  success: 'text-green-500',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function NotificationCenter() {
  const { t } = useTranslation();
  const { notificationPanelOpen, toggleNotificationPanel } = useUIStore();
  const { subscribe } = useSSEStore();
  const user = useAuthStore(s => s.user);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<any>(`/v3/notifications?seller_id=${useUIStore.getState().sellerId}`);
      if (res.success) {
        setNotifications(res.data?.notifications || []);
        setUnread(res.data?.unread || 0);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  // Load on open
  useEffect(() => {
    if (notificationPanelOpen) loadNotifications();
  }, [notificationPanelOpen, loadNotifications]);

  // SSE: listen for new notifications
  useEffect(() => {
    const unsub = subscribe('notification', () => {
      setUnread(prev => prev + 1);
      if (notificationPanelOpen) loadNotifications();
    });
    return unsub;
  }, [subscribe, notificationPanelOpen, loadNotifications]);

  // Load unread count on mount
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const markAllRead = async () => {
    try {
      await api.post('/v3/notifications/read-all', { seller_id: useUIStore.getState().sellerId });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const markRead = async (id: number) => {
    try {
      await api.post(`/v3/notifications/${id}/read`, {});
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  // Badge for TopBar (rendered via portal-like pattern)
  useEffect(() => {
    const badge = document.getElementById('notification-badge');
    if (badge) {
      badge.textContent = unread > 0 ? String(unread > 99 ? '99+' : unread) : '';
      badge.style.display = unread > 0 ? 'flex' : 'none';
    }
  }, [unread]);

  if (!notificationPanelOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={toggleNotificationPanel} />
      <div className="fixed right-0 top-0 h-full w-full sm:w-80 bg-[var(--color-surface)] border-l border-[var(--color-border)] shadow-lg z-50 flex flex-col">
        <div className="flex items-center justify-between px-4 h-[var(--topbar-height)] border-b border-[var(--color-border)] shrink-0">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-[var(--color-text-secondary)]" />
            <h2 className="font-semibold text-[var(--color-text)]">{t('notifications.title')}</h2>
            {unread > 0 && (
              <span className="text-xs bg-red-500 text-white rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                {unread}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)]"
                title={t('notifications.markAllRead')}
              >
                <CheckCheck size={16} />
              </button>
            )}
            <button onClick={toggleNotificationPanel} className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)]">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-5 h-5 animate-spin text-[var(--color-text-muted)]" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-[var(--color-text-muted)]">
              <Bell size={40} className="mb-3 opacity-30" />
              <p className="text-sm">{t('notifications.noNotifications')}</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--color-border)]">
              {notifications.map(n => {
                const Icon = TYPE_ICONS[n.type] || Info;
                const color = TYPE_COLORS[n.type] || 'text-blue-500';
                return (
                  <div
                    key={n.id}
                    onClick={() => !n.read && markRead(n.id)}
                    className={`px-4 py-3 cursor-pointer hover:bg-[var(--color-surface-hover)] transition-colors ${
                      !n.read ? 'bg-[var(--color-primary-light)]' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <Icon size={16} className={`mt-0.5 flex-shrink-0 ${color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm font-medium text-[var(--color-text)] ${!n.read ? 'font-semibold' : ''}`}>
                            {n.title}
                          </span>
                          <span className="text-xs text-[var(--color-text-muted)] flex-shrink-0">
                            {timeAgo(n.created_at)}
                          </span>
                        </div>
                        {n.message && (
                          <p className="text-xs text-[var(--color-text-secondary)] mt-0.5 line-clamp-2">
                            {n.message}
                          </p>
                        )}
                      </div>
                      {!n.read && (
                        <span className="w-2 h-2 rounded-full bg-[var(--color-primary)] flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
